import type { TFunction } from 'i18next';
import type { i18n as I18nInstance } from 'i18next';

export const normalizeExcelHeader = (value: unknown) => {
    if (value === null || value === undefined) return '';
    const raw =
        typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
    return raw
        .trim()
        .replaceAll(/\s+/g, ' ')
        .toLowerCase()
        .normalize('NFD')
        .replaceAll(/[\u0300-\u036f]/g, '');
};

const collectLanguages = (i18n: I18nInstance) => {
    const languageSet = new Set<string>();
    i18n.languages?.forEach((lng) => languageSet.add(lng));
    const supportedLngs = Array.isArray(i18n.options?.supportedLngs) ? i18n.options?.supportedLngs : [];
    supportedLngs.forEach((lng: string) => languageSet.add(lng));
    Object.keys(i18n.store?.data ?? {}).forEach((lng) => languageSet.add(lng));
    return Array.from(languageSet).filter((lng) => lng && lng !== 'cimode');
};

const addTranslationMappings = (
    mapping: Record<string, string>,
    jsonKey: string,
    translationKeys: string[],
    languages: string[],
    t: TFunction,
    i18n: I18nInstance,
    addMapping: (label: string, key: string) => void
) => {
    translationKeys.forEach((translationKey) => {
        const currentLabel = String(t(`excel.${translationKey}`));
        if (currentLabel && !currentLabel.includes('.')) {
            addMapping(currentLabel, jsonKey);
        }

        languages.forEach((lng) => {
            const label = String(i18n.t(`excel.${translationKey}`, { lng, defaultValue: '' }));
            if (label && !label.includes('.')) {
                addMapping(label, jsonKey);
            }
        });
    });
};

export const buildExcelColumnMapping = (
    excelColumnKeys: string[],
    translationKeysByJsonKey: Record<string, string[]>,
    t: TFunction,
    i18n: I18nInstance
): Record<string, string> => {
    const mapping: Record<string, string> = {};
    const addMapping = (label: string, jsonKey: string) => {
        const normalized = normalizeExcelHeader(label);
        if (normalized) mapping[normalized] = jsonKey;
    };
    const languages = collectLanguages(i18n);

    excelColumnKeys.forEach((jsonKey) => {
        addMapping(jsonKey, jsonKey);
        const translationKeys = translationKeysByJsonKey[jsonKey] ?? [jsonKey];
        addTranslationMappings(mapping, jsonKey, translationKeys, languages, t, i18n, addMapping);
    });

    return mapping;
};

const pad2 = (value: number) => String(value).padStart(2, '0');

export const unwrapExcelCellValue = (value: any): any => {
    if (value === null || value === undefined) return value;
    if (typeof value !== 'object' || value instanceof Date) return value;
    if ('result' in value) return unwrapExcelCellValue(value.result);
    if (Array.isArray(value.richText)) {
        return value.richText.map((part: any) => part?.text ?? '').join('');
    }
    if (typeof value.text === 'string') return value.text;
    return value;
};

export const getReadableCellValue = (cell: any): any => {
    const raw = unwrapExcelCellValue(cell?.value);
    if (raw instanceof Date || typeof raw === 'number' || typeof raw === 'boolean') {
        return raw;
    }
    if (typeof raw === 'string' && raw.trim()) {
        return raw.trim();
    }
    const text = typeof cell?.text === 'string' ? cell.text.trim() : '';
    return text || raw;
};

const excelSerialToUtcDate = (serial: number): Date =>
    new Date(Date.UTC(1899, 11, 30) + Math.round(serial * 86400000));

const toIsoDate = (year: number, month: number, day: number): string => {
    const normalizedYear = year < 100 ? year + 2000 : year;
    if (!Number.isFinite(normalizedYear) || !Number.isFinite(month) || !Number.isFinite(day)) return '';
    if (month < 1 || month > 12 || day < 1 || day > 31) return '';
    return `${normalizedYear}-${pad2(month)}-${pad2(day)}`;
};

const formatUtcDate = (date: Date): string => {
    if (Number.isNaN(date.getTime())) return '';
    return toIsoDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
};

const toHhMm = (hours: number, minutes: number): string => {
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '';
    const normalizedHours = ((Math.floor(hours) % 24) + 24) % 24;
    const normalizedMinutes = ((Math.floor(minutes) % 60) + 60) % 60;
    return `${pad2(normalizedHours)}:${pad2(normalizedMinutes)}`;
};

const formatUtcTime = (date: Date): string => {
    if (Number.isNaN(date.getTime())) return '';
    return toHhMm(date.getUTCHours(), date.getUTCMinutes());
};

const normalizeTimeString = (value: string) => value.trim().replaceAll(/\s+/g, ' ');

const parseTwentyFourHourTime = (value: string): string => {
    const match = /^([01]?\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/.exec(value);
    if (!match) return '';
    return toHhMm(Number(match[1]), Number(match[2]));
};

const parseTwelveHourTime = (value: string): string => {
    const match = /^(1[0-2]|0?[1-9]):([0-5]\d)(?::([0-5]\d))?\s*(AM|PM)$/i.exec(value);
    if (!match) return '';
    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const period = match[4].toUpperCase();
    if (period === 'AM') {
        if (hours === 12) hours = 0;
    } else if (hours !== 12) {
        hours += 12;
    }
    return toHhMm(hours, minutes);
};

export const formatExcelDateValue = (value: any): string => {
    const unwrapped = unwrapExcelCellValue(value);
    if (unwrapped === null || unwrapped === undefined || unwrapped === '') return '';

    if (unwrapped instanceof Date) {
        return formatUtcDate(unwrapped);
    }

    if (typeof unwrapped === 'number' && Number.isFinite(unwrapped) && unwrapped >= 1) {
        return formatUtcDate(excelSerialToUtcDate(unwrapped));
    }

    const strValue = String(unwrapped).trim();
    if (!strValue) return '';

    const datePart = strValue.split(/[T\s]/)[0];
    const parts = datePart.split(/[./-]/);
    if (parts.length !== 3) return '';

    const [first, second, third] = parts;
    if (!first || !second || !third) return '';

    if (first.length === 4) {
        return toIsoDate(Number(first), Number(second), Number(third));
    }

    return toIsoDate(Number(third), Number(second), Number(first));
};

export const formatExcelTimeValue = (value: any): string => {
    const unwrapped = unwrapExcelCellValue(value);
    if (unwrapped === null || unwrapped === undefined || unwrapped === '') return '';

    if (unwrapped instanceof Date) {
        return formatUtcTime(unwrapped);
    }

    if (typeof unwrapped === 'number' && Number.isFinite(unwrapped)) {
        const dayFraction = ((unwrapped % 1) + 1) % 1;
        const totalMinutes = Math.round(dayFraction * 24 * 60);
        return toHhMm(Math.floor(totalMinutes / 60) % 24, totalMinutes % 60);
    }

    const strValue = normalizeTimeString(String(unwrapped));
    if (!strValue) return '';

    return parseTwelveHourTime(strValue) || parseTwentyFourHourTime(strValue);
};

export const extractWorksheetHeaders = (worksheet: any): string[] => {
    const headers: string[] = [];
    worksheet.getRow(1).eachCell({ includeEmpty: true }, (cell: any, colNumber: number) => {
        headers[colNumber - 1] = cell.value?.toString()?.trim() ?? '';
    });
    return headers;
};

const assignCellToRowData = (
    rowData: any,
    headers: string[],
    columnMapping: Record<string, string>,
    cell: any,
    colNumber: number
) => {
    const header = headers[colNumber - 1];
    if (!header) return;
    const jsonKey = columnMapping[normalizeExcelHeader(header)];
    if (jsonKey) {
        rowData[jsonKey] = getReadableCellValue(cell);
    }
};

export const parseWorksheetRowsToJson = (
    worksheet: any,
    headers: string[],
    columnMapping: Record<string, string>
): any[] => {
    const jsonData: any[] = [];
    worksheet.eachRow((row: any, rowNumber: number) => {
        if (rowNumber === 1) return;
        const rowData: any = {};
        row.eachCell({ includeEmpty: true }, (cell: any, colNumber: number) => {
            assignCellToRowData(rowData, headers, columnMapping, cell, colNumber);
        });
        if (Object.keys(rowData).length > 0) {
            jsonData.push(rowData);
        }
    });
    return jsonData;
};

export const mapRowsToCutOffList = (jsonData: any[]) =>
    jsonData.map((row: any) => {
        const validFrom = formatExcelDateValue(row?.validFromDate);
        const validTo = formatExcelDateValue(row?.validToDate);

        return {
            departureLocationId: Number(row?.departureLocationId) || 0,
            departureLocationName: String(row?.departureLocationName ?? ''),
            arrivalLocationId: Number(row?.arrivalLocationId) || 0,
            arrivalLocationName: String(row?.arrivalLocationName ?? ''),
            distanceRangeId: Number(row?.distanceRangeId) || 0,
            distanceRangeName: String(row?.distanceRangeName ?? ''),
            cutoffTime: formatExcelTimeValue(row?.cutoffTime),
            commitmentDay: Number(row?.commitmentDay) || 0,
            ...(validFrom ? { validFromDate: validFrom } : {}),
            ...(validTo ? { validToDate: validTo } : {}),
            isActive: row?.isActive === true || row?.isActive === 'true' || row?.isActive === 1,
            deliveryType: String(row?.deliveryType ?? ''),
            deliveryTypeId: Number(row?.deliveryTypeId) || 0,
            addressTypeId: Number(row?.addressTypeId) || 0,
            addressTypeName: String(row?.addressTypeName ?? ''),
        };
    });
